/**
 * Instafeed Mobile Carousel
 * Converts Instafeed app grid to mobile carousel with touch/swipe, arrows, dots, and auto-scroll
 */

(function() {
  'use strict';

  const MOBILE_BREAKPOINT = 768; // md breakpoint
  const AUTO_SCROLL_INTERVAL = 4000; // 4 seconds
  const MIN_SWIPE_DISTANCE = 50;

  let carouselInitialized = false;
  let currentSlide = 0;
  let slideCount = 0;
  let touchStart = null;
  let touchEnd = null;
  let isDragging = false;
  let autoPlayInterval = null;
  let observer = null;

  /**
   * Check if viewport is mobile
   */
  function isMobile() {
    return window.innerWidth < MOBILE_BREAKPOINT;
  }

  /**
   * Initialize carousel on mobile
   */
  function initCarousel() {
    const instaFeed = document.getElementById('insta-feed');
    if (!instaFeed) return false;

    const wrapper = instaFeed.querySelector('.instafeed-new-layout-wrapper');
    if (!wrapper) return false;

    const items = wrapper.querySelectorAll('.instafeed-new-layout-item');
    if (items.length === 0) return false;

    // Check if already initialized
    if (instaFeed.querySelector('.instafeed-carousel-wrapper')) {
      return true;
    }

    // Only initialize on mobile
    if (!isMobile()) {
      return false;
    }

    slideCount = items.length;
    currentSlide = 0;

    // Create carousel wrapper
    const carouselWrapper = document.createElement('div');
    carouselWrapper.className = 'instafeed-carousel-wrapper';

    // Create slider container
    const slider = document.createElement('div');
    slider.className = 'instafeed-carousel-slider';

    // Create slides container
    const slides = document.createElement('div');
    slides.className = 'instafeed-carousel-slides';

    // Clone and wrap items
    items.forEach((item, index) => {
      const slide = document.createElement('div');
      slide.className = 'instafeed-carousel-slide';
      slide.setAttribute('data-slide-index', index);

      const slideContent = document.createElement('div');
      slideContent.className = 'instafeed-carousel-slide-content';
      
      // Deep clone the item to preserve all nested elements
      const clonedItem = item.cloneNode(true);
      // Remove inline styles that might conflict with carousel layout
      if (clonedItem.style) {
        clonedItem.style.width = '';
        clonedItem.style.height = '';
        clonedItem.style.clipPath = '';
      }
      
      // Fix lazy-loaded images: copy data-instafeed-src to src
      const lazyImages = clonedItem.querySelectorAll('img.instafeed-lazy-image');
      lazyImages.forEach(img => {
        const actualSrc = img.getAttribute('data-instafeed-src');
        if (actualSrc) {
          img.src = actualSrc;
          // Remove lazy loading classes/attributes if needed
          img.classList.remove('instafeed-lazy-image--handled');
        }
      });
      
      slideContent.appendChild(clonedItem);
      slide.appendChild(slideContent);
      slides.appendChild(slide);
    });

    slider.appendChild(slides);

    // Create navigation arrows
    const prevBtn = document.createElement('button');
    prevBtn.className = 'instafeed-carousel-arrow instafeed-carousel-arrow-prev';
    prevBtn.setAttribute('aria-label', 'Previous Instagram post');
    prevBtn.innerHTML = `
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
      </svg>
    `;

    const nextBtn = document.createElement('button');
    nextBtn.className = 'instafeed-carousel-arrow instafeed-carousel-arrow-next';
    nextBtn.setAttribute('aria-label', 'Next Instagram post');
    nextBtn.innerHTML = `
      <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
      </svg>
    `;

    slider.appendChild(prevBtn);
    slider.appendChild(nextBtn);

    // Create dots indicator
    const dotsContainer = document.createElement('div');
    dotsContainer.className = 'instafeed-carousel-dots';

    for (let i = 0; i < slideCount; i++) {
      const dot = document.createElement('button');
      dot.className = 'instafeed-carousel-dot';
      if (i === 0) dot.classList.add('active');
      dot.setAttribute('data-slide', i);
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dotsContainer.appendChild(dot);
    }

    carouselWrapper.appendChild(slider);
    carouselWrapper.appendChild(dotsContainer);

    // Insert carousel before the original wrapper
    wrapper.parentNode.insertBefore(carouselWrapper, wrapper);

    // Setup carousel functionality
    setupCarouselEvents(slider, slides, prevBtn, nextBtn, dotsContainer);

    return true;
  }

  /**
   * Setup carousel event handlers
   */
  function setupCarouselEvents(slider, slides, prevBtn, nextBtn, dotsContainer) {
    // Update slider position
    function updateSlider() {
      slides.style.transform = `translateX(-${currentSlide * 100}%)`;

      // Update dots
      const dots = dotsContainer.querySelectorAll('.instafeed-carousel-dot');
      dots.forEach((dot, index) => {
        if (index === currentSlide) {
          dot.classList.add('active');
        } else {
          dot.classList.remove('active');
        }
      });
    }

    // Next slide
    function nextSlide() {
      currentSlide = (currentSlide === slideCount - 1) ? 0 : currentSlide + 1;
      updateSlider();
    }

    // Previous slide
    function prevSlide() {
      currentSlide = (currentSlide === 0) ? slideCount - 1 : currentSlide - 1;
      updateSlider();
    }

    // Go to specific slide
    function goToSlide(index) {
      if (index >= 0 && index < slideCount) {
        currentSlide = index;
        updateSlider();
      }
    }

    // Touch events for mobile swipe
    slider.addEventListener('touchstart', function(e) {
      touchEnd = null;
      touchStart = e.targetTouches[0].clientX;
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
      }
    }, { passive: true });

    slider.addEventListener('touchmove', function(e) {
      touchEnd = e.targetTouches[0].clientX;
    }, { passive: true });

    slider.addEventListener('touchend', function() {
      if (!touchStart || !touchEnd) {
        startAutoPlay();
        return;
      }

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
      const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

      if (isLeftSwipe) {
        nextSlide();
      }
      if (isRightSwipe) {
        prevSlide();
      }

      startAutoPlay();
    }, { passive: true });

    // Mouse events for cursor dragging (desktop testing)
    slider.addEventListener('mousedown', function(e) {
      isDragging = true;
      touchEnd = null;
      touchStart = e.clientX;
      slider.style.cursor = 'grabbing';
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
      }
    });

    slider.addEventListener('mousemove', function(e) {
      if (!isDragging) return;
      touchEnd = e.clientX;
    });

    slider.addEventListener('mouseup', function() {
      if (!isDragging) return;
      isDragging = false;
      slider.style.cursor = 'grab';

      if (!touchStart || !touchEnd) {
        startAutoPlay();
        return;
      }

      const distance = touchStart - touchEnd;
      const isLeftSwipe = distance > MIN_SWIPE_DISTANCE;
      const isRightSwipe = distance < -MIN_SWIPE_DISTANCE;

      if (isLeftSwipe) {
        nextSlide();
      }
      if (isRightSwipe) {
        prevSlide();
      }

      startAutoPlay();
    });

    slider.addEventListener('mouseleave', function() {
      isDragging = false;
      slider.style.cursor = 'grab';
      startAutoPlay();
    });

    // Button events
    prevBtn.addEventListener('click', function() {
      prevSlide();
      resetAutoPlay();
    });

    nextBtn.addEventListener('click', function() {
      nextSlide();
      resetAutoPlay();
    });

    // Dot events
    dotsContainer.addEventListener('click', function(e) {
      if (e.target.classList.contains('instafeed-carousel-dot')) {
        const slideIndex = parseInt(e.target.getAttribute('data-slide'));
        goToSlide(slideIndex);
        resetAutoPlay();
      }
    });

    // Pause auto-play on hover
    slider.addEventListener('mouseenter', function() {
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
      }
    });

    slider.addEventListener('mouseleave', function() {
      startAutoPlay();
    });

    // Auto-scroll
    function startAutoPlay() {
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
      }
      autoPlayInterval = setInterval(() => {
        nextSlide();
      }, AUTO_SCROLL_INTERVAL);
    }

    function resetAutoPlay() {
      if (autoPlayInterval) {
        clearInterval(autoPlayInterval);
      }
      startAutoPlay();
    }

    // Initialize
    updateSlider();
    startAutoPlay();
  }

  /**
   * Remove carousel and restore grid
   */
  function removeCarousel() {
    const instaFeed = document.getElementById('insta-feed');
    if (!instaFeed) return;

    const carouselWrapper = instaFeed.querySelector('.instafeed-carousel-wrapper');
    if (carouselWrapper) {
      carouselWrapper.remove();
    }

    // Show grid
    const wrapper = instaFeed.querySelector('.instafeed-new-layout-wrapper');
    if (wrapper) {
      wrapper.style.display = '';
    }

    if (autoPlayInterval) {
      clearInterval(autoPlayInterval);
      autoPlayInterval = null;
    }

    carouselInitialized = false;
  }

  /**
   * Handle window resize
   */
  function handleResize() {
    const wasMobile = carouselInitialized;
    const isNowMobile = isMobile();

    if (isNowMobile && !wasMobile) {
      // Switched to mobile, initialize carousel
      if (initCarousel()) {
        carouselInitialized = true;
      }
    } else if (!isNowMobile && wasMobile) {
      // Switched to desktop, remove carousel
      removeCarousel();
    }
  }

  /**
   * Check and initialize carousel when content loads
   */
  function checkAndInit() {
    if (isMobile() && !carouselInitialized) {
      if (initCarousel()) {
        carouselInitialized = true;
      }
    } else if (!isMobile() && carouselInitialized) {
      removeCarousel();
    }
  }

  /**
   * Initialize observer to watch for Instafeed content
   */
  function initObserver() {
    const instaFeed = document.getElementById('insta-feed');
    if (!instaFeed) {
      // Retry after a short delay if #insta-feed doesn't exist yet
      setTimeout(initObserver, 500);
      return;
    }

    // Check immediately
    checkAndInit();

    // Watch for changes
    observer = new MutationObserver(function(mutations) {
      let shouldCheck = false;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          // Check if new items were added
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) { // Element node
              if (node.classList && (
                node.classList.contains('instafeed-new-layout-item') ||
                node.querySelector && node.querySelector('.instafeed-new-layout-item')
              )) {
                shouldCheck = true;
              }
            }
          });
        }
      });

      if (shouldCheck) {
        // Small delay to ensure DOM is updated
        setTimeout(checkAndInit, 100);
      }
    });

    observer.observe(instaFeed, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Initialize on DOM ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initObserver);
    } else {
      initObserver();
    }

    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', function() {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(handleResize, 250);
    });
  }

  // Start initialization
  init();
})();

